import React from "react";
import { Table, Pagination, Form, Button, Badge } from "react-bootstrap";
import { HiOutlineArrowSmLeft, HiOutlineArrowSmRight } from "react-icons/hi";
import { FaTable, FaFilter } from 'react-icons/fa';

const ModernTable = ({ columns, data, actions, pagination, onRowClick }) => {

  const headerStyles = {
    background: 'linear-gradient(135deg, rgba(143, 173, 21, 0.85) 0%, rgba(28, 66, 32, 0.95) 100%)',
    color: 'white',
    fontWeight: '600',
    fontSize: '0.92rem',
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    padding: '12px 16px',
    border: 'none',
    textAlign: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 10
  };

  const cellStyles = {
    padding: '10px 16px',
    fontSize: '0.9rem',
    borderBottom: '1px solid #e9ecef',
    backgroundColor: 'white',
    transition: 'all 0.2s ease',
    textAlign: 'center',
    verticalAlign: 'middle'
  };

  const rowHoverStyles = {
    backgroundColor: 'rgba(240, 247, 230, 0.5)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(140, 173, 21, 0.12)'
  };

  const noDataStyles = {
    padding: '60px 20px',
    fontSize: '1.1rem',
    color: '#6c757d',
    fontWeight: '500',
    backgroundColor: 'rgba(248, 249, 250, 0.5)',
    border: '2px dashed #dee2e6'
  };

  return (
    <div className="mt-2 position-relative">
      <div style={{ 
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 6px 24px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(143, 173, 21, 0.1)',
        background: '#ffffff'
      }}>
        <div className="table-responsive">
          <Table
            hover
            responsive
            className="mb-0"
            style={{ borderCollapse: 'separate', borderSpacing: 0 }}
          >
            <thead>
              <tr>
                {columns.map((col, idx) => (
                  <th key={idx} style={headerStyles}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="text-center"
                    style={noDataStyles}
                  >
                    <FaTable size={40} style={{ opacity: 0.15, marginBottom: '15px' }} />
                    <div>Nenhum dado encontrado</div>
                    <small className="text-muted mt-2 d-block">Verifique os filtros ou tente novamente</small>
                  </td>
                </tr>
              ) : (
                data.map((row, i) => (
                  <tr
                    key={i}
                    style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                    onClick={() => onRowClick && onRowClick(i)}
                    onMouseEnter={(e) => {
                      if(onRowClick) Object.assign(e.currentTarget.style, rowHoverStyles);
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {row.map((cell, j) => {
                      // Se é a última coluna e temos ações, renderizar os botões
                      if (j === row.length - 1 && actions) {
                        return (
                          <td key={j} style={cellStyles}>
                            {actions(data[i], i)}
                          </td>
                        );
                      }
                      return (
                        <td key={j} style={cellStyles}>
                          {cell}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {/* Renderizar paginação se fornecida */}
        {pagination && (
          <div
            className="d-flex justify-content-between align-items-center p-3"
            style={{ 
              backgroundImage: 'linear-gradient(180deg, #f8f9fa, #ffffff)',
              borderTop: '1px solid rgba(222, 226, 230, 0.6)'
            }}
          >
            <div className="d-flex align-items-center">
              <div className="d-flex align-items-center bg-light p-1 rounded border">
                <Form.Label className="text-muted me-2 mb-0 ps-1">Linhas:</Form.Label>
                <Form.Select
                  value={pagination.rowsPerPage}
                  onChange={pagination.onChangeRowsPerPage}
                  size="sm"
                  style={{ 
                    width: '70px',
                    borderRadius: '6px',
                    border: 'none',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                  className="bg-white"
                >
                  {[10, 20, 40, 100].map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>

            <Badge
              bg="light"
              text="dark"
              className="px-3 py-2 d-flex align-items-center"
              style={{ 
                border: '1px solid #dee2e6', 
                fontSize: '0.85rem',
                boxShadow: '0 2px 5px rgba(0,0,0,0.03)',
                fontWeight: '500'
              }}
            >
              <FaFilter size={10} className="me-2" />
              {pagination.currentStart + 1}-{Math.min(pagination.currentEnd, pagination.total)} de {pagination.total}
            </Badge>

            <Pagination className="mb-0">
              <Pagination.Prev
                onClick={() => pagination.onChangePage(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 0}
              >
                <HiOutlineArrowSmLeft />
              </Pagination.Prev>

              {pagination.pageNumbers
                .filter(num => {
                  const current = pagination.currentPage;
                  const total = pagination.totalPages;
                  if (total <= 7) return true;
                  if (num === 0 || num === total - 1) return true;
                  return num >= current - 2 && num <= current + 2;
                })
                .map((pageNum, idx, array) => (
                  <React.Fragment key={pageNum}>
                    {idx > 0 && pageNum !== array[idx - 1] + 1 && (
                      <Pagination.Ellipsis disabled />
                    )}
                    <Pagination.Item
                      active={pageNum === pagination.currentPage}
                      onClick={() => pagination.onChangePage(pageNum)}
                      style={pageNum === pagination.currentPage ? {
                        background: 'linear-gradient(135deg, rgba(143, 173, 21, 0.9) 0%, rgba(28, 66, 32, 0.95) 100%)',
                        borderColor: 'rgba(28, 66, 32, 0.4)',
                        boxShadow: '0 2px 6px rgba(28, 66, 32, 0.2)'
                      } : {}}
                    >
                      {pageNum + 1}
                    </Pagination.Item>
                  </React.Fragment>
                ))}

              <Pagination.Next
                onClick={() => pagination.onChangePage(pagination.currentPage + 1)}
                disabled={pagination.currentPage >= pagination.totalPages - 1}
              >
                <HiOutlineArrowSmRight />
              </Pagination.Next>
            </Pagination>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModernTable;
